import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertCircle, CheckCircle, Truck } from 'lucide-react';
import { Can } from '../components/Auth/Can';
import OrdersTable from '../components/DespachoNacionales/OrdersTable';
import TicketModal from '../components/DespachoNacionales/TicketModal';
import PreviewFacturaLocalModal from '../components/DespachoNacionales/PreviewFacturaLocalModal';
import ConfirmInventarioModal from '../components/DespachoNacionales/ConfirmInventarioModal';
import { useFacturaLocalPolling } from '../hooks/useFacturaLocalPolling';
import {
  fetchNacionalOrders,
  fetchInventarioStatus,
  rebajarInventario,
  previewFacturaLocalNacional,
  selectAllOrdersHaveTickets,
  selectCanRebajarInventario,
  selectPreviewData,
  selectGeneratingFactura,
  selectSyncEventStatus
} from '../store/slices/despachoNacionalesSlice';
import './DespachoNacionales.css';

const DespachoNacionales = () => {
  const dispatch = useDispatch();
  const selectedSeason = useSelector((state) => state.season.selected?.season);
  const selectedWeek = useSelector((state) => state.season.selected?.week);
  const orders = useSelector((state) => state.despachoNacionales.orders);
  const loading = useSelector((state) => state.despachoNacionales.loading);
  const error = useSelector((state) => state.despachoNacionales.error);
  const inventarioRebajado = useSelector((state) => state.despachoNacionales.inventarioRebajado);
  const inventarioData = useSelector((state) => state.despachoNacionales.inventarioData);
  const rebajandoInventario = useSelector((state) => state.despachoNacionales.rebajandoInventario);
  const allOrdersHaveTickets = useSelector(selectAllOrdersHaveTickets);
  const canRebajarInventario = useSelector(selectCanRebajarInventario);

  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [rebajarError, setRebajarError] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedOrderForPreview, setSelectedOrderForPreview] = useState(null);

  // Selectors for Factura Local
  const previewData = useSelector(selectPreviewData);
  const generatingFactura = useSelector(selectGeneratingFactura);
  const syncEventStatus = useSelector(selectSyncEventStatus);

  // Enable polling for factura local generation status
  useFacturaLocalPolling();

  console.log('[DespachoNacionales] Render:', { 
    selectedSeason, 
    selectedWeek, 
    hasId: !!selectedSeason?.id,
    seasonId: selectedSeason?.id,
    condition: !!(selectedSeason?.id && selectedWeek)
  });

  useEffect(() => {
    if (selectedSeason?.anio && selectedWeek) {
      console.log('[DespachoNacionales] Fetching orders for:', { 
        temporadaId: selectedSeason.anio, 
        semana: selectedWeek,
        selectedSeason 
      });
      dispatch(fetchNacionalOrders({ temporadaId: selectedSeason.anio, semana: selectedWeek }));
      dispatch(fetchInventarioStatus({ temporadaId: selectedSeason.anio, semana: selectedWeek }));
    }
  }, [dispatch, selectedSeason, selectedWeek]);

  const handleOpenTicketModal = (order) => {
    setSelectedOrder(order);
    setTicketModalOpen(true);
  };

  const handleCloseTicketModal = () => {
    setTicketModalOpen(false);
    setSelectedOrder(null);
  };

  const handleOpenConfirmModal = () => {
    setRebajarError(null);
    setConfirmModalOpen(true);
  };

  const handleCloseConfirmModal = () => {
    setConfirmModalOpen(false);
    setRebajarError(null);
  };

  const handleOpenPreviewModal = (order) => {
    setSelectedOrderForPreview(order);
    setPreviewModalOpen(true);
    // Dispatch preview action to fetch invoice data
    const fecha = new Date().toISOString().split('T')[0];
    dispatch(previewFacturaLocalNacional({ pedidoId: order.id, fecha }));
  };

  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
    setSelectedOrderForPreview(null);
  };

  const handleConfirmRebajar = async () => {
    try {
      setRebajarError(null);
      const result = await dispatch(
        rebajarInventario({
          temporadaId: selectedSeason.anio,
          semana: selectedWeek
        })
      ).unwrap();
      
      // Check if any products resulted in deficit
      const deficitProducts = result?.productos_con_deficit || [];
      const deficitCount = deficitProducts.length;
      
      if (deficitCount > 0) {
        const deficitMessage = `⚠️ ADVERTENCIA: ${deficitCount} producto${deficitCount > 1 ? 's' : ''} quedó${deficitCount > 1 ? '' : 'ó'} con stock negativo.\n\nProductos afectados:\n${deficitProducts.map(p => `- ${p.nombre}: ${p.stock_resultante} cajas`).join('\n')}\n\nPor favor, revise estos productos en la sección de Inventario y corrija los déficits.`;
        alert(deficitMessage);
      }
      
      handleCloseConfirmModal();
    } catch (error) {
      console.error('Error al rebajar inventario:', error);
      const errorMessage = error?.message || error?.error || error || 'Error al rebajar inventario';
      setRebajarError(errorMessage);
    }
  };

  const ordersWithTickets = orders.filter(o => o.ticket_despacho);
  const totalOrders = orders.length;

  if (!selectedSeason || !selectedWeek) {
    return (
      <div className="despacho-nacionales-container">
        <div className="despacho-nacionales-empty">
          <AlertCircle size={48} />
          <p>Seleccione una temporada y semana para ver los pedidos nacionales</p>
        </div>
      </div>
    );
  }

  return (
    <div className="despacho-nacionales-container">
      <div className="despacho-nacionales-header">
        <div className="header-title">
          <Truck size={32} />
          <h1>Despacho Nacionales</h1>
        </div>
      </div>

      <div className="despacho-nacionales-status">
        <div className="status-card">
          <div className="status-label">Pedidos con Ticket</div>
          <div className="status-value">
            {ordersWithTickets.length} / {totalOrders}
          </div>
          {allOrdersHaveTickets && totalOrders > 0 && (
            <div className="status-badge success">
              <CheckCircle size={16} />
              <span>Todos los tickets ingresados</span>
            </div>
          )}
        </div>

        <div className="status-card">
          <div className="status-label">Estado Inventario</div>
          {inventarioRebajado ? (
            <div className="status-badge rebajado">
              <CheckCircle size={16} />
              <span>Inventario Rebajado</span>
              {inventarioData && (
                <div className="status-details">
                  <small>
                    {new Date(inventarioData.fecha_ejecucion).toLocaleString('es-CL')}
                  </small>
                  <small>{inventarioData.total_cajas} cajas</small>
                </div>
              )}
            </div>
          ) : (
            <div className="status-badge pendiente">
              <AlertCircle size={16} />
              <span>Pendiente</span>
            </div>
          )}
        </div>

        <Can I="execute" a="despacho-nacionales:rebajar-inventario">
          <div className="status-card action-card">
            <button
              className="btn-rebajar-inventario"
              onClick={handleOpenConfirmModal}
              disabled={!canRebajarInventario || rebajandoInventario || totalOrders === 0}
            >
              {rebajandoInventario ? 'Rebajando...' : 'Rebajar Inventario'}
            </button>
            {!allOrdersHaveTickets && totalOrders > 0 && (
              <small className="action-hint">
                Todos los pedidos deben tener ticket de despacho
              </small>
            )}
          </div>
        </Can>
      </div>

      {error && (
        <div className="despacho-nacionales-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="despacho-nacionales-loading">
          <div className="spinner"></div>
          <p>Cargando pedidos nacionales...</p>
        </div>
      ) : totalOrders === 0 ? (
        <div className="despacho-nacionales-empty">
          <Truck size={48} />
          <p>No hay pedidos nacionales para esta semana</p>
        </div>
      ) : (
        <OrdersTable 
          orders={orders} 
          onOpenTicketModal={handleOpenTicketModal}
          onOpenPreviewModal={handleOpenPreviewModal}
          generatingFactura={generatingFactura}
          syncEventStatus={syncEventStatus}
        />
      )}

      {ticketModalOpen && (
        <TicketModal
          order={selectedOrder}
          onClose={handleCloseTicketModal}
          temporadaId={selectedSeason.id}
          semana={selectedWeek}
        />
      )}

      {confirmModalOpen && (
        <ConfirmInventarioModal
          onClose={handleCloseConfirmModal}
          onConfirm={handleConfirmRebajar}
          temporada={selectedSeason.anio}
          semana={selectedWeek}
          totalOrders={totalOrders}
          totalCajas={orders.reduce(
            (sum, order) =>
              sum + (order.detalles?.reduce((s, d) => {
                // Exclude Despacho products from box count
                if (d.productos?.category === 'Despacho') {
                  return s;
                }
                return s + d.cantidad_cajas;
              }, 0) || 0),
            0
          )}
          loading={rebajandoInventario}
          error={rebajarError}
        />
      )}

      {previewModalOpen && selectedOrderForPreview && (
        <PreviewFacturaLocalModal
          isOpen={previewModalOpen}
          invoiceData={previewData}
          pedidoId={selectedOrderForPreview.id}
          fecha={new Date().toISOString().split('T')[0]}
          onClose={handleClosePreviewModal}
          isGenerating={generatingFactura[selectedOrderForPreview.id] || false}
        />
      )}
    </div>
  );
};

export default DespachoNacionales;
